import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBanner } from '@platform/ui';
import { ModuleStringsProvider } from '@platform/modularity/ModuleStringsProvider';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { BaElsewhereNote } from '@platform/engine/insights/BaElsewhereNote';
import { PageFrame } from '@platform/engine/list/header/PageFrame';
import { tableTitle } from '@platform/engine/list/header/tableTitle';
import { useModuleCrumbs } from '@platform/engine/list/header/useModuleCrumbs';
import { ViewPageHead } from '@platform/engine/list/header/ViewPageHead';
import { useRecordListModel } from '@platform/engine/list/useRecordListModel';
import type { IMenuEntry } from '@platform/engine/menu/IMenuEntry';
import { AnalyticsBody } from './analytics/AnalyticsBody';
import type { IModulePageProps } from './IModulePageProps';

/**
 * Аналітика модуля (прототип v4): крихти, «Модуль — Таблиця» над заголовком «Аналітика», «Показники й діаграми цієї таблиці.», рядок
 * «Фільтр» + «Розширені фільтри» і панель тегів і пошуку; тіло — аналітика головного типу запису (AnalyticsBody: показники й діаграми
 * прототипу за записами з фільтрами сторінки) на стандартній моделі списку з власним ключем стану `page:analytics`. Записи аналітика
 * вантажить сама, тож сторінку списку модель не запитує (skipPage).
 */
export function ModuleAnalyticsPage({ ws, module, rt }: IModulePageProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings(module.module.key);
  const entry = useMemo((): IMenuEntry => ({ key: 'page:analytics', label: t('modulePages.analytics'), group: '', view: 'dashboard' }), [t]);
  const model = useRecordListModel(ws, module, rt, entry, 'dashboard', { skipPage: true });
  const moduleName = strings.moduleName(module.module.name);
  const crumbs = useModuleCrumbs(module.module.key, moduleName);
  const eyebrow = tableTitle(moduleName, strings.entity(rt.key, 'name', rt.name));
  return (
    <ModuleStringsProvider strings={strings}>
      <PageFrame crumbs={crumbs} head={<ViewPageHead model={model} eyebrow={eyebrow} title={t('modulePages.analytics')} description={t('pageHead.analyticsDesc')} />}>
        <BaElsewhereNote ws={ws} moduleKey={module.module.key} rtKey={rt.key} />
        {model.error && <ErrorBanner error={model.error} />}
        <AnalyticsBody model={model} />
      </PageFrame>
    </ModuleStringsProvider>
  );
}
